import { useCallback, useState } from 'react';

export interface UseNoticeResult {
  /** The message waiting to be shown, or null when there is nothing to say. */
  notice: string | null;
  setNotice: (message: string) => void;
  clearNotice: () => void;
}

/**
 * One line of app-level news, for the things that happen *to* a session rather
 * than because of a click: an island deleted underneath you, a permission
 * revoked, guest access running out, an account deactivated (§6.5, §6.6).
 *
 * All of those end with the screen changing — back to the island list, or out
 * to the sign-in modal — so the message cannot live with whatever was on screen
 * when it arrived. It lives in the app context instead, which outlives both the
 * island and the session, and the screen the user lands on renders it.
 *
 * Deliberately one message, not a queue: these events end a session or a visit,
 * so a second one has nothing left to interrupt, and the newest is the one that
 * explains where the user just ended up.
 */
export function useNotice(): UseNoticeResult {
  const [notice, setNoticeState] = useState<string | null>(null);

  const setNotice = useCallback((message: string) => setNoticeState(message), []);
  const clearNotice = useCallback(() => setNoticeState(null), []);

  return { notice, setNotice, clearNotice };
}
